import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { ChatWidget, BackToTop } from '../components/ChatAndUtils';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Download, FileText, ArrowLeft, Loader2, Clock, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api, useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const Downloads = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState('');
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/downloads/catalog');
        setCategories(data.categories || []);
        if (data.categories?.length) setActiveTab(data.categories[0].category);
      } catch (e) {
        toast({
          title: t('downloads.toast.error_title'),
          description: t('downloads.toast.error_description'),
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [toast, t]);

  const handleDownload = async (doc) => {
    if (!doc.available) return;
    if (!isAuthenticated) { navigate(`/login?next=${encodeURIComponent('/downloads')}`); return; }
    setDownloading(doc.slug);
    try {
      const response = await api.get(`/downloads/file/${doc.slug}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      const ext = (doc.filename || '').split('.').pop() || 'pdf';
      link.href = url;
      link.setAttribute('download', `${doc.label}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast({
        title: t('downloads.toast.download_failed_title'),
        description: e?.response?.data?.detail || t('downloads.toast.download_failed_description'),
        variant: 'destructive',
      });
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title={t('downloads.seo.title')}
        description={t('downloads.seo.description')}
        path="/downloads"
      />
      <Header />
      <main className="pt-[150px] pb-20 bg-[#f5f8fa] min-h-[80vh]">
        <section className="py-10">
          <div className="max-w-5xl mx-auto px-6 lg:px-10">
            <Link
              to={isAuthenticated ? '/dashboard' : '/'}
              className="inline-flex items-center gap-1 text-[#34536b] text-sm hover:text-[#b8893d] mb-4"
              data-testid="downloads-back-link"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> {isAuthenticated ? t('downloads.back_link') : t('downloads.back_home')}
            </Link>
            <div className="mb-8">
              <div className="flex items-center gap-3 text-[#b8893d] text-xs tracking-[0.3em] mb-3">
                <span className="w-12 h-px bg-[#b8893d]"></span>
                {t('downloads.header.section_label')}
              </div>
              <h1 className="font-serif text-3xl md:text-4xl text-[#1B3A5C]">
                {t('downloads.header.title')}
              </h1>
              <p className="text-[#34536b] text-sm mt-2 max-w-2xl leading-relaxed">
                {t('downloads.header.description')}
              </p>
            </div>

            {loading ? (
              <div className="text-center py-16">
                <Loader2 className="w-8 h-8 text-[#b8893d] animate-spin mx-auto" />
              </div>
            ) : categories.length === 0 ? (
              <div className="bg-white p-12 text-center rounded-sm border-2 border-dashed border-gray-200">
                <FileText className="w-12 h-12 text-[#b8893d]/40 mx-auto mb-4" />
                <h3 className="text-[#1B3A5C] font-serif text-xl mb-2">{t('downloads.empty_state.title')}</h3>
                <p className="text-[#34536b] text-sm">{t('downloads.empty_state.description')}</p>
              </div>
            ) : (
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList
                  className="flex flex-wrap h-auto bg-white border border-gray-200 p-1.5 rounded-sm gap-1 justify-start"
                  data-testid="downloads-tabs-list"
                >
                  {categories.map((c) => (
                    <TabsTrigger
                      key={c.category}
                      value={c.category}
                      data-testid={`tab-${c.category}`}
                      className="data-[state=active]:bg-[#1B3A5C] data-[state=active]:text-white text-[#34536b] tracking-wider text-[11px] px-3 py-2 rounded-sm whitespace-normal text-left max-w-full"
                    >
                      {c.category.toUpperCase()}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {categories.map((c) => (
                  <TabsContent key={c.category} value={c.category} className="mt-6">
                    <div className="bg-white rounded-sm shadow-sm overflow-hidden">
                      {c.documents.map((doc, idx) => {
                        const isDownloading = downloading === doc.slug;
                        return (
                          <div
                            key={doc.slug}
                            className={`flex flex-wrap sm:flex-nowrap items-center gap-4 p-5 ${
                              idx > 0 ? 'border-t border-gray-100' : ''
                            } ${doc.available ? 'hover:bg-[#fafaf7] transition-colors' : 'opacity-70'}`}
                            data-testid={`download-row-${doc.slug}`}
                          >
                            <div
                              className={`w-12 h-12 rounded-sm flex items-center justify-center flex-shrink-0 ${
                                doc.available ? 'bg-[#fbf7f0]' : 'bg-gray-100'
                              }`}
                            >
                              {doc.available ? (
                                <FileText className="w-5 h-5 text-[#b8893d]" />
                              ) : (
                                <Clock className="w-5 h-5 text-gray-400" />
                              )}
                            </div>
                            <div className="flex-1 min-w-[calc(100%-4rem)] sm:min-w-0">
                              <p className="text-[#1B3A5C] font-semibold text-sm">{doc.label}</p>
                              {doc.description && (
                                <p className="text-[#34536b] text-xs mt-1 leading-relaxed">{doc.description}</p>
                              )}
                              <div className="flex items-center gap-3 mt-1.5">
                                {doc.available ? (
                                  <>
                                    <span className="text-[10px] text-[#34536b] tracking-wider uppercase">
                                      {(doc.filename || '').split('.').pop() || 'PDF'}
                                    </span>
                                    <span className="text-[10px] text-[#34536b]">·</span>
                                    <span className="text-[10px] text-[#34536b]">{formatSize(doc.size)}</span>
                                  </>
                                ) : (
                                  <span className="text-[10px] bg-[#b8893d]/10 text-[#b8893d] px-2 py-0.5 rounded-sm tracking-wider">
                                    {t('downloads.document.coming_soon')}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex-shrink-0 w-full sm:w-auto pl-16 sm:pl-0">
                              <Button
                                type="button"
                                onClick={() => handleDownload(doc)}
                                disabled={!doc.available || isDownloading}
                                data-testid={`download-btn-${doc.slug}`}
                                className="bg-[#b8893d] hover:bg-[#a07730] text-white tracking-wider text-[11px] px-4 disabled:opacity-40"
                              >
                                {isDownloading ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  !isAuthenticated && doc.available ? (
                                    <><Lock className="w-3.5 h-3.5 mr-1.5" /> {t('services_section.login_short', 'ANMELDEN')}</>
                                  ) : (
                                    <><Download className="w-3.5 h-3.5 mr-1.5" /> {t('downloads.document.download_button')}</>
                                  )
                                )}
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <ChatWidget />
      <BackToTop />
      <Toaster />
    </div>
  );
};

export default Downloads;
